// Studby — Photo to Quiz generator. Photo of notes → 20 MCQs → auto-graded score.
// Saved quizzes can be retaken for free (no AI cost).
import { useState, useRef, useEffect, useCallback } from "react";
import { Camera, Loader2, CheckCircle2, XCircle, RotateCcw, History, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Paywall, TrialBanner } from "@/components/Paywall";

export default function QuizGenerator() {
  const { hasAccess } = useAuth();
  const [fileName, setFileName] = useState("");
  const [quiz, setQuiz] = useState(null);        // { quiz_id, questions }
  const [answers, setAnswers] = useState({});     // { index: optionIndex }
  const [result, setResult] = useState(null);     // graded result
  const [loading, setLoading] = useState(false);
  const [grading, setGrading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState([]);         // saved quizzes list
  const inputRef = useRef(null);

  const loadSaved = useCallback(() => {
    api.get("/quizzes").then(({ data }) => setSaved(data.quizzes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasAccess) loadSaved();
  }, [hasAccess, loadSaved]);

  if (!hasAccess) return <Paywall />;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setLoading(true);
    setError("");
    setQuiz(null);
    setResult(null);
    setAnswers({});
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/quiz/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQuiz(data);
      loadSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Retake a previously generated quiz — free, no AI call.
  const retake = async (quizId) => {
    setError("");
    setResult(null);
    setAnswers({});
    try {
      const { data } = await api.get(`/quizzes/${quizId}`);
      setQuiz(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const submit = async () => {
    setGrading(true);
    setError("");
    try {
      const ordered = quiz.questions.map((_, i) => (answers[i] ?? -1));
      const { data } = await api.post("/quiz/submit", {
        quiz_id: quiz.quiz_id,
        answers: ordered,
      });
      setResult(data);
      loadSaved();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGrading(false);
    }
  };

  const restart = () => {
    setQuiz(null);
    setResult(null);
    setAnswers({});
    setFileName("");
  };

  const answeredCount = Object.keys(answers).length;

  return (
    <div>
      <TrialBanner />
      <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-1">Notes → Quiz</h1>
      <p className="text-muted-foreground mb-6">Snap a photo or upload a PDF of your notes to get a 10-question quiz.</p>

      {/* Score result */}
      {result && (
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 mb-6 text-center" data-testid="quiz-result">
          <p className="text-sm opacity-80 uppercase tracking-widest">Your score</p>
          <p className="font-heading text-5xl font-extrabold my-2" data-testid="quiz-score">{result.score}%</p>
          <p className="opacity-90">{result.correct} / {result.total} correct</p>
          <Button variant="secondary" onClick={restart} className="rounded-full mt-5" data-testid="new-quiz-button">
            <RotateCcw size={16} className="mr-1.5" aria-hidden="true" /> New quiz
          </Button>
        </div>
      )}

      {/* Upload */}
      {!quiz && (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          data-testid="pdf-upload-area"
          className="w-full border-2 border-dashed border-border rounded-2xl bg-card py-14 flex flex-col items-center gap-3 hover:border-accent transition-colors disabled:opacity-60"
        >
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary">
            {loading ? <Loader2 size={26} className="animate-spin" aria-hidden="true" /> : <Camera size={26} aria-hidden="true" />}
          </span>
          <span className="font-medium">
            {loading ? "Reading your notes & writing questions…" : "Tap to take a photo or upload a PDF"}
          </span>
          {fileName && !loading && <span className="text-xs text-muted-foreground">{fileName}</span>}
        </button>
      )}
      <input
        ref={inputRef} type="file" accept="application/pdf,image/*"
        onChange={upload} className="hidden" data-testid="pdf-input"
      />

      {error && <p className="text-sm text-destructive mt-4" data-testid="quiz-error">{error}</p>}

      {/* Saved quizzes — retake for free (no AI cost) */}
      {!quiz && saved.length > 0 && (
        <div className="mt-8" data-testid="saved-quizzes">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <History size={18} className="text-primary" aria-hidden="true" /> Your saved quizzes
          </h2>
          <div className="space-y-2.5">
            {saved.map((s) => (
              <div
                key={s.quiz_id}
                data-testid={`saved-quiz-${s.quiz_id}`}
                className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {s.count}-question quiz
                    {s.completed && s.score != null && (
                      <span className="ml-2 text-xs font-semibold text-accent">Best: {s.score}%</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => retake(s.quiz_id)}
                  className="rounded-full shrink-0"
                  data-testid={`retake-${s.quiz_id}`}
                >
                  <RefreshCw size={14} className="mr-1.5" aria-hidden="true" /> Retake
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Retaking a saved quiz is free and doesn't use up your questions.
          </p>
        </div>
      )}

      {/* Questions */}
      {quiz && (
        <div className="space-y-5">
          {quiz.questions.map((q, qi) => {
            const res = result?.results?.[qi];
            return (
              <div key={qi} className="bg-card border border-border rounded-2xl p-5" data-testid={`question-${qi}`}>
                <p className="font-medium mb-3">
                  <span className="text-muted-foreground mr-1">{qi + 1}.</span> {q.question}
                </p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const picked = answers[qi] === oi;
                    let cls = "border-border";
                    if (result) {
                      if (oi === res.answer_index) cls = "border-accent bg-accent/10";
                      else if (picked && !res.is_correct) cls = "border-destructive bg-destructive/10";
                    } else if (picked) {
                      cls = "border-primary bg-secondary";
                    }
                    return (
                      <button
                        key={oi}
                        disabled={!!result}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        data-testid={`q${qi}-option-${oi}`}
                        className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${cls} ${!result ? "hover:border-primary" : ""}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {res && (
                  <p className="mt-3 text-sm flex items-start gap-1.5 text-muted-foreground">
                    {res.is_correct
                      ? <CheckCircle2 size={16} className="text-accent mt-0.5 shrink-0" aria-hidden="true" />
                      : <XCircle size={16} className="text-destructive mt-0.5 shrink-0" aria-hidden="true" />}
                    {res.explanation}
                  </p>
                )}
              </div>
            );
          })}

          {!result && (
            <Button
              onClick={submit}
              disabled={grading || answeredCount < quiz.questions.length}
              className="w-full rounded-full"
              data-testid="submit-quiz-button"
            >
              {grading
                ? (<><Loader2 size={18} className="mr-1.5 animate-spin" aria-hidden="true" /> Grading…</>)
                : `Submit quiz (${answeredCount}/${quiz.questions.length})`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
